import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * SEOHead Component - Dynamically updates Open Graph and Twitter Card meta tags
 * @param {string} title - Page title
 * @param {string} description - Meta description (max 160 chars recommended)
 * @param {string} image - OG image URL (defaults to company logo)
 * @param {string} type - OG type (website, article, product)
 * @param {string} canonical - Canonical URL override
 */
export default function SEOHead({ 
  title, 
  description, 
  image = 'https://www.iqeslowvoltage.com/logo-iqes-new.png',
  type = 'website',
  canonical
}) {
  const location = useLocation();
  const baseUrl = 'https://www.iqeslowvoltage.com';
  const currentUrl = canonical || `${baseUrl}${location.pathname}`;
  
  const fullTitle = title 
    ? `${title} | IQES Low Voltage` 
    : 'IQES Low Voltage Solutions | Fiber Optic, CCTV, Cabling | Florida';
  
  const metaDescription = description || 
    'IQES - Professional low voltage solutions in Florida. CCTV, fiber optic, cabling, access control. 13+ years experience. Free quotes!';

  useEffect(() => {
    // Update document title
    document.title = fullTitle;
    
    // Helper to update or create meta tag
    const updateMeta = (selector, content, attribute = 'content') => {
      let element = document.querySelector(selector);
      if (element) {
        element.setAttribute(attribute, content);
      }
    };
    
    // Update meta description
    updateMeta('meta[name="description"]', metaDescription);
    
    // Update Open Graph tags
    updateMeta('meta[property="og:title"]', fullTitle);
    updateMeta('meta[property="og:description"]', metaDescription);
    updateMeta('meta[property="og:url"]', currentUrl);
    updateMeta('meta[property="og:image"]', image);
    updateMeta('meta[property="og:type"]', type);
    
    // Update Twitter Card tags
    updateMeta('meta[property="twitter:title"]', fullTitle);
    updateMeta('meta[property="twitter:description"]', metaDescription);
    updateMeta('meta[property="twitter:url"]', currentUrl);
    updateMeta('meta[property="twitter:image"]', image);
    
    // Update canonical
    updateMeta('link[rel="canonical"]', currentUrl, 'href');
    
  }, [fullTitle, metaDescription, currentUrl, image, type]);

  return null; // This component only manages document head
}
